<template>
  <config-cycle
    :billing-cycle="billingCycle"
    :billing-cycle-list="billingCycleList"
    :unit="planUnitMap.user"
    @switchCycleAction="handleBillingCycleChange"
    @syncCycle="syncCycle"
    :show-cycle-alert="showCycleAlert"
  />
</template>

<script>
import { compact, isEmpty } from 'lodash';
import { mapGetters } from 'vuex';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { plansof } from '@/utils/chain';
import { isEmptyVal } from '@/utils';
import { BILLING_CYCLE, BillingCycleEnum, limitBillingCycleWithBaseZoneTypes } from '@/common/constants';
import PbxMixin from '@/plans/ZONE_PRO_PLUS/mixin/pbx-mixin';
import LimitMixin from '@/plans/ZONE_PRO_PLUS/mixin/limit-mixin';
import { planUnitMap } from '@/common/product-unit';

export default {
  name: 'plan-billing-cycle',
  components: { ConfigCycle },
  mixins: [PbxMixin, LimitMixin],
  model: {
    prop: 'billingCycle',
    event: 'syncCycle',
  },
  props: {
    categoryList: Array,
    billingCycle: String,
    considerTimeBaseCouponWithBasePlan: Boolean,
    monthlyDisabledAlertMessage: String,
  },
  data() {
    return {
      planUnitMap: Object.freeze(planUnitMap),
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getUserCurrentSubscriptions', 'planMapFlat', 'disableMonthly2AnnualTip', 'boughtPlans']),
    maxAddonCycleValue() {
      const newLimit = [];
      return Math.max(...this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type)
      )).map(item => item.cycleValue));
    },
    showCycleAlert() {
      return !isEmptyVal(this.monthlyDisabledAlertMessage);
    },
    monthlyPlan() {
      const basicItem = {
        plan: null,
        disabled: false,
        tooltips: '',
        cycleValue: BillingCycleEnum[BILLING_CYCLE.MONTHLY]
      };

      basicItem.plan = plansof(this.categoryList).monthly().get(0);
      if (isEmptyVal(basicItem.plan)) return null;

      if (this.annualAddonPlans.length) {
        basicItem.disabled = true;
        basicItem.tooltips = this.$t('billing.planform.monthly_as_base_disable_tooltip', [this.allLimitAnnualPlanNames.join(','), this.$t('billing.zone_pro_plus.title')]);
      }

      if (!isEmptyVal(this.monthlyDisabledAlertMessage)) {
        basicItem.disabled = true;
        basicItem.alert = this.monthlyDisabledAlertMessage;
      }

      return basicItem;
    },
    annualPlan() {
      const annualPlan = plansof(this.categoryList).annual().get(0);
      if (isEmptyVal(annualPlan)) return null;

      const basicItem = {
        plan: annualPlan,
        disabled: false,
        disabledTooltip: '',
        cycleValue: BillingCycleEnum[BILLING_CYCLE.ANNUAL]
      };

      if (this.considerTimeBaseCouponWithBasePlan && this.disableMonthly2AnnualTip) {
        basicItem.disabled = true;
        basicItem.tooltips = this.disableMonthly2AnnualTip;
      }

      return basicItem;
    },
    quarterlyPlan() {
      const quarterlyPlan = plansof(this.categoryList).quarterly().get(0);
      if (isEmpty(quarterlyPlan)) return null;

      return { plan: quarterlyPlan, disabled: false, disabledTooltip: '', cycleValue: BillingCycleEnum[BILLING_CYCLE.QUARTERLY] };
    },
    twoYearsPlan() {
      const twoYearsPlan = plansof(this.categoryList).twoYears().get(0);
      if (isEmpty(twoYearsPlan)) return null;

      return { plan: twoYearsPlan, disabled: false, disabledTooltip: '', cycleValue: BillingCycleEnum[BILLING_CYCLE.TWO_YEARS] };
    },
    threeYearsPlan() {
      const threeYearsPlan = plansof(this.categoryList).threeYears().get(0);
      if (isEmpty(threeYearsPlan)) return null;

      return { plan: threeYearsPlan, disabled: false, disabledTooltip: '', cycleValue: BillingCycleEnum[BILLING_CYCLE.THREE_YEARS] };
    },
    billingCycleList() {
      return compact([this.monthlyPlan, this.quarterlyPlan, this.annualPlan, this.twoYearsPlan, this.threeYearsPlan]).map(p => {
        let disabled = p.disabled;
        if (BillingCycleEnum[this.billingCycle] === p.cycleValue) {
          disabled = false;
        } else if (p.cycleValue < this.maxAddonCycleValue) {
          disabled = true;
        }
        return ({
          ...p,
          disabled
        });
      });
    },
  },
  methods: {
    handleBillingCycleChange(oldPlan, newPlan) {
      const billingCycle = newPlan.billing_cycle;
      this.$tracking.buyInteraction('Zoom One Pro Plus subscription cycle', billingCycle);
      const selectedBasePlanCategoryNumberMap = this.getSelectedBasePlanCategoryNumberMap()
      const selectedCategoryNumberMap = this.getSelectedCategoryNumberMap();
      this.switchPlansBillingCycle(selectedCategoryNumberMap, billingCycle);

      if (this.selectedPbxBasePlan?.billing_cycle !== BILLING_CYCLE.PAY_AS_YOU_GO) {
        this.switchPlansBillingCycle(selectedBasePlanCategoryNumberMap, billingCycle);
        this.$emit('updatePbxBasePlanCode', this.selectedPbxBasePlan.plan_code);
      }
    },
    syncCycle(value) {
      this.$emit('syncCycle', value);
    },
  },
};
</script>
